import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  ChangeEvent,
  Followable,
  Json,
  LiveData,
  RegsIndex,
  RegsWhere,
  Sector,
  Species,
} from '../domain/regs-models';

export class RegsApiException extends Error {
  constructor(public readonly status: number, public readonly path: string) {
    super(
      status === 401
        ? 'The app key was not accepted (HTTP 401).'
        : `Regulations unavailable (HTTP ${status}).`
    );
    this.name = 'RegsApiException';
  }
}

export class RegsStaleException extends Error {
  constructor(public readonly said: string, public readonly today: string) {
    super(`The server answered for ${said}, not today (${today}).`);
    this.name = 'RegsStaleException';
  }
}

interface CacheEntry {
  fetchedAt: Date;
  answer: any;
  savedCopyAt?: Date;
}

@Injectable({ providedIn: 'root' })
export class RegsApiService {
  private http = inject(HttpClient);
  private readonly cache = new Map<string, CacheEntry>();
  private distrustUntil: Date | null = null;

  get baseUrl(): string {
    return environment.apiUrl.replace(/\/api\/v1\/?$/, '');
  }

  private get isDistrusted(): boolean {
    return this.distrustUntil != null && new Date() < this.distrustUntil;
  }

  clearCache(prefix = ''): void {
    if (!prefix) {
      this.cache.clear();
      return;
    }
    for (const k of Array.from(this.cache.keys())) {
      if (k.startsWith(prefix)) this.cache.delete(k);
    }
  }

  private async getJson<T>(
    path: string,
    params: Record<string, string> = {},
    keepMs = 10 * 60 * 1000
  ): Promise<T> {
    const qStr = Object.entries(params)
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&');
    const cacheKey = `${path}?${qStr}`;
    const hit = this.cache.get(cacheKey);

    if (hit && Date.now() - hit.fetchedAt.getTime() < keepMs) {
      return hit.answer as T;
    }

    let httpParams = new HttpParams();
    for (const [k, v] of Object.entries(params)) {
      httpParams = httpParams.set(k, v);
    }

    const url = `${this.baseUrl}${path}`;
    try {
      const resp = await firstValueFrom(this.http.get<Json>(url, { params: httpParams }));
      this.cache.set(cacheKey, { fetchedAt: new Date(), answer: resp });
      return resp as T;
    } catch (err: any) {
      const status = err?.status || 500;
      throw new RegsApiException(status, path);
    }
  }

  async index(): Promise<RegsIndex> {
    const json = await this.getJson<Json>('/regs/index.json');
    return new RegsIndex(json);
  }

  async species(id: string): Promise<Species> {
    const json = await this.getJson<Json>(`/regs/species/${id}.json`);
    return new Species(json);
  }

  async live(id: string, sector: Sector): Promise<LiveData> {
    const json = await this.getJson<Json>(`/regs/live/${id}.json`, { sector }, 5 * 60 * 1000);
    return new LiveData(json);
  }

  async where(lat: number, lon: number): Promise<RegsWhere | null> {
    try {
      const json = await this.getJson<Json>(
        '/regs/where.json',
        {
          lat: lat.toFixed(3),
          lon: lon.toFixed(3),
        },
        10 * 60 * 1000
      );
      if (typeof json?.['kind'] === 'string') {
        return RegsWhere.fromServer(json);
      }
    } catch (e) {
      console.warn('RegsApiService: /regs/where.json unavailable', e);
    }
    return null;
  }

  async followables(): Promise<Followable[]> {
    const json = await this.getJson<Json>('/regs/followables.json', {}, 6 * 60 * 60 * 1000);
    const items = Array.isArray(json?.['items']) ? json['items'] : [];
    return items.map((e: Json) => new Followable(e));
  }

  async changes(follow: string[] = [], since?: Date, limit = 50): Promise<ChangeEvent[]> {
    if (this.isDistrusted) {
      throw new Error('the server is answering for another day');
    }

    const parts = this.followParts(follow);
    if (parts.length > 1) {
      const byId = new Map<string, ChangeEvent>();
      for (const p of parts) {
        const events = await this.fetchChanges(p, since, limit);
        for (const e of events) {
          byId.set(e.id, e);
        }
      }
      return Array.from(byId.values()).sort((a, b) => b.at.getTime() - a.at.getTime());
    }

    return this.fetchChanges(follow, since, limit);
  }

  private followParts(follow: string[]): string[][] {
    const budget = 2500;
    const parts: string[][] = [];
    let part: string[] = [];
    let size = 0;

    for (const f of follow) {
      const n = encodeURIComponent(f).length + 3;
      if (part.length > 0 && size + n > budget) {
        parts.push(part);
        part = [];
        size = 0;
      }
      part.push(f);
      size += n;
    }
    if (part.length > 0) parts.push(part);
    return parts;
  }

  private async fetchChanges(follow: string[], since?: Date, limit: number = 50): Promise<ChangeEvent[]> {
    const params: Record<string, string> = { limit: String(limit) };
    if (follow.length > 0) params['follow'] = follow.join(',');
    if (since) params['since'] = since.toISOString();

    const json = await this.getJson<Json>('/regs/changes.json', params, 30 * 1000);
    const events = Array.isArray(json?.['events']) ? json['events'] : [];
    return events.map((e: Json) => new ChangeEvent(e));
  }
}
